import type { McpConnection } from '@/types/mcp'

const DAY = 24 * 60 * 60 * 1000
const EXPIRY_WARNING_MS = 7 * DAY
const ENDED_LISTED_FOR_MS = 30 * DAY

const time = (iso: string | null) => (iso ? Date.parse(iso) : NaN)

/** An expired connection has no `revokedAt`; it simply ran past `expiresAt`. */
export function endedAt(connection: McpConnection): string {
  return connection.revokedAt ?? connection.expiresAt
}

export function isExpiringSoon(connection: McpConnection, now = Date.now()): boolean {
  return connection.isLive && time(connection.expiresAt) - now <= EXPIRY_WARNING_MS
}

export function endReason(connection: McpConnection): string {
  if (!connection.revokedAt) return 'Expired'
  switch (connection.revokeReason) {
    case 'user':
    case 'admin':
      return connection.revokedBy ? `Disconnected by ${connection.revokedBy}` : 'Disconnected'
    case 'accessRemoved':
      return 'Access removed'
    case 'replay':
      return 'Ended for security'
    default:
      return 'Ended'
  }
}

/** As the API orders them: live first, then newest connected first. */
export function orderConnections(connections: McpConnection[], now = Date.now()): McpConnection[] {
  const newestConnected = (a: McpConnection, b: McpConnection) => time(b.connectedAt) - time(a.connectedAt)
  const live = connections.filter((connection) => connection.isLive).sort(newestConnected)
  const ended = connections
    .filter((connection) => !connection.isLive && now - time(endedAt(connection)) <= ENDED_LISTED_FOR_MS)
    .sort(newestConnected)
  return [...live, ...ended]
}
